import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import * as React from "react";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@replohq/lattice/components/field/Field";
import { cn } from "@replohq/lattice/utils/cn";

interface LabeledControlProps<ClassName extends string = string>
  extends React.PropsWithChildren {
  label?: string;
  labelAction?: React.ReactNode;
  id?: string;
  description?: React.ReactNode;
  error?: string;
  required?: boolean;
  size?: "sm" | "base";
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function LabeledControl<ClassName extends string>({
  label,
  labelAction,
  id,
  description,
  error,
  required = false,
  size = "base",
  children,
  className,
  UNSAFE_className,
}: LabeledControlProps<ClassName>) {
  return (
    <Field className={cn(className, UNSAFE_className)} size="sm">
      {/* NOTE (Ben, 2025-01-15): Using flex-col-reverse so the input comes first in tab order
            but the label displays first visually. This ensures that when tabbing through a form,
            you go directly to the next input instead of any label actions (like "Forgot Password?"). */}
      <div className="flex flex-col-reverse gap-[inherit]">
        {children}
        {(label || labelAction) && (
          <div className="flex items-center justify-between gap-1.5">
            {label && (
              <FieldLabel htmlFor={id} size={size} UNSAFE_className="gap-1">
                {required && (
                  <span className="text-rose ml-0.5" aria-hidden="true">
                    *
                  </span>
                )}
                {label}
              </FieldLabel>
            )}
            {labelAction}
          </div>
        )}
      </div>
      {description && !error && (
        <FieldDescription size={size}>{description}</FieldDescription>
      )}
      {error && <FieldError size={size}>{error}</FieldError>}
    </Field>
  );
}

export { LabeledControl };
